package ru.villawonka.app.domain.model

import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

/**
 * Фильтры для поиска
 */
@Serializable
sealed class SearchParamsDomain {
    abstract val city: String?
    abstract val districts: List<String>?
    abstract val country: CountryDomain?

    /**
     * Базовый интерфейс поиска сдачи в аренду
     */
    @Serializable
    sealed class RentalItem : SearchParamsDomain() {
        abstract val costMin: Int?
        abstract val costMax: Int?

        /**
         * Фильтры поиска жилья
         */
        @Serializable
        data class Accommodation(
            override val costMin: Int? = null,
            override val costMax: Int? = null,
            override val city: String? = null,
            override val districts: List<String> = emptyList(),
            override val country: CountryDomain? = null,
            val accommodationTypes: List<AccommodationTypeDomain> = emptyList(),
            val additions: List<AccommodationAdditionDomain> = emptyList(),
            val bedrooms: List<String> = emptyList(),
        ) : RentalItem()

        /**
         * Фильтры поиска транспорта
         */
        @Serializable
        data class Transport(
            override val costMin: Int? = null,
            override val costMax: Int? = null,
            override val city: String? = null,
            override val districts: List<String> = emptyList(),
            override val country: CountryDomain? = null,
        ) : RentalItem()
    }

    companion object {

        @OptIn(ExperimentalEncodingApi::class)
        fun fromBase64(base64: String): SearchParamsDomain? {
            val json = Base64.decode(base64).decodeToString()
            return Json.decodeFromString(json)
        }
    }
}


@OptIn(ExperimentalEncodingApi::class)
fun SearchParamsDomain.toBase64(): String {
    val json = Json.encodeToString(this)
    val base64 = Base64.encode(json.encodeToByteArray())
    return base64
}

fun SearchParamsDomain.clear() = when (this) {
    is SearchParamsDomain.RentalItem.Accommodation -> SearchParamsDomain.RentalItem.Accommodation()
    is SearchParamsDomain.RentalItem.Transport -> SearchParamsDomain.RentalItem.Transport()
}

val SearchParamsDomain.hasCustomFilters: Boolean
    get() = when (this) {
        is SearchParamsDomain.RentalItem.Accommodation -> SearchParamsDomain.RentalItem.Accommodation() != this
        is SearchParamsDomain.RentalItem.Transport -> SearchParamsDomain.RentalItem.Transport() != this
    }

/**
 * Сравнивает два [SearchParamsDomain] по их полям (структурное сравнение)
 */
fun SearchParamsDomain.contentEquals(other: SearchParamsDomain): Boolean {
    if (this::class != other::class) return false

    return when (this) {
        is SearchParamsDomain.RentalItem.Accommodation -> {
            val o = other as SearchParamsDomain.RentalItem.Accommodation
            this.costMin == o.costMin &&
                    this.costMax == o.costMax &&
                    this.city == o.city &&
                    this.districts == o.districts &&
                    this.country == o.country &&
                    this.accommodationTypes == o.accommodationTypes &&
                    this.additions == o.additions &&
                    this.bedrooms == o.bedrooms
        }
        is SearchParamsDomain.RentalItem.Transport -> {
            val o = other as SearchParamsDomain.RentalItem.Transport
            this.costMin == o.costMin &&
                    this.costMax == o.costMax &&
                    this.city == o.city &&
                    this.districts == o.districts &&
                    this.country == o.country
        }
    }
}